import JSZip from 'jszip';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function processXhtmlElement(element: Element): string {
  const tagName = element.tagName.toLowerCase();

  switch (tagName) {
    case 'p':
      const pContent = processXhtmlContent(element);
      return pContent.trim() ? `<p>${pContent}</p>` : '';
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      const level = parseInt(tagName[1], 10);
      const hContent = processXhtmlContent(element);
      return hContent.trim() ? `<h${level}>${hContent}</h${level}>` : '';
    }
    case 'strong':
    case 'b':
      return `<strong>${processXhtmlContent(element)}</strong>`;
    case 'em':
    case 'i':
      return `<em>${processXhtmlContent(element)}</em>`;
    case 'u':
      return `<u>${processXhtmlContent(element)}</u>`;
    case 's':
    case 'strike':
      return `<s>${processXhtmlContent(element)}</s>`;
    case 'br':
      return '<br>';
    case 'a': {
      const href = element.getAttribute('href') || '';
      return `<a href="${escapeHtml(href)}">${processXhtmlContent(element)}</a>`;
    }
    case 'ul':
    case 'ol': {
      const listTag = tagName;
      let result = `<${listTag}>`;
      const items = element.querySelectorAll(':scope > li');
      for (const item of Array.from(items)) {
        const itemContent = processXhtmlContent(item);
        if (itemContent.trim()) {
          result += `<li>${itemContent}</li>`;
        }
      }
      result += `</${listTag}>`;
      return result;
    }
    case 'li': {
      let result = '';
      for (const child of Array.from(element.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          result += escapeHtml(child.textContent || '');
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          result += processXhtmlElement(child as Element);
        }
      }
      return result;
    }
    case 'blockquote':
      return `<blockquote>${processXhtmlContent(element)}</blockquote>`;
    case 'div':
    case 'span':
      return processXhtmlContent(element);
    case 'section':
      return processXhtmlContent(element);
    default:
      return processXhtmlContent(element);
  }
}

function processXhtmlContent(element: Element): string {
  let result = '';
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      result += escapeHtml(child.textContent || '');
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      result += processXhtmlElement(child as Element);
    }
  }
  return result;
}

function extractBodyFromXhtml(xhtmlContent: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xhtmlContent, 'application/xhtml+xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    return '';
  }

  const body = doc.querySelector('body');
  if (!body) {
    return '';
  }

  let html = '';
  for (const child of Array.from(body.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      html += processXhtmlElement(child as Element);
    } else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
      html += `<p>${escapeHtml(child.textContent.trim())}</p>`;
    }
  }

  return html;
}

async function parseContainerXml(zip: JSZip): Promise<string> {
  const containerXml = await zip.file('META-INF/container.xml')?.async('text');
  if (!containerXml) {
    throw new Error('Could not find META-INF/container.xml in EPUB file');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(containerXml, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Failed to parse container.xml');
  }

  const rootfile = doc.querySelector('rootfile');
  const contentPath = rootfile?.getAttribute('full-path');

  if (!contentPath) {
    throw new Error('Could not find content.opf path in container.xml');
  }

  return contentPath;
}

async function parseContentOpf(zip: JSZip, contentPath: string): Promise<{ spineItems: string[]; basePath: string }> {
  const contentOpf = await zip.file(contentPath)?.async('text');
  if (!contentOpf) {
    throw new Error(`Could not find ${contentPath} in EPUB file`);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(contentOpf, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Failed to parse content.opf');
  }

  const basePath = contentPath.substring(0, contentPath.lastIndexOf('/') + 1);

  const manifestItems = new Map<string, string>();
  const manifestItemsElements = doc.querySelectorAll('manifest item');
  for (const item of Array.from(manifestItemsElements)) {
    const id = item.getAttribute('id');
    const href = item.getAttribute('href');
    if (id && href) {
      manifestItems.set(id, href);
    }
  }

  const spineItems: string[] = [];
  const itemrefs = doc.querySelectorAll('spine itemref');
  for (const itemref of Array.from(itemrefs)) {
    const idref = itemref.getAttribute('idref');
    if (idref) {
      const href = manifestItems.get(idref);
      if (href) {
        spineItems.push(href);
      }
    }
  }

  return { spineItems, basePath };
}

export async function convertEpubToHtml(arrayBuffer: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(arrayBuffer);

  const contentPath = await parseContainerXml(zip);
  const { spineItems, basePath } = await parseContentOpf(zip, contentPath);

  if (spineItems.length === 0) {
    throw new Error('No spine items found in EPUB file');
  }

  let fullHtml = '';

  for (const spineItem of spineItems) {
    const fullPath = basePath + spineItem;
    const xhtmlContent = await zip.file(fullPath)?.async('text');

    if (!xhtmlContent) {
      continue;
    }

    const bodyHtml = extractBodyFromXhtml(xhtmlContent);
    if (bodyHtml.trim()) {
      fullHtml += bodyHtml;
    }
  }

  if (!fullHtml.trim()) {
    throw new Error('EMPTY_FILE');
  }

  return fullHtml;
}

